import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { detectPreviewKind } from "../../controllers/useDocDetail";

// The renderers pull in pdf.js, docx-preview and xlsx — roughly a megabyte. Loading that only when a
// preview is actually opened keeps it out of the initial bundle for everyone who never opens one.
const DocPreview = lazy(() => import("../screens/eqms/DocPreview").then((m) => ({ default: m.DocPreview })));

/**
 * Reads a document without downloading it: one modal every document surface can open.
 *
 * The caller supplies `load`, so the modal does not care whether the bytes come from a 문서함 revision,
 * an 외부문서 upload or an attachment — it only needs the filename to pick a renderer.
 */
export function DocPreviewModal({
  filename,
  format,
  load,
  onDownload,
  onClose,
  subtitle,
}: {
  filename: string;
  /** The document's declared format, used when the filename has no useful extension. */
  format?: string | null;
  load: () => Promise<ArrayBuffer>;
  onDownload: () => void;
  onClose: () => void;
  /** A line under the title — which revision this is, say. */
  subtitle?: string;
}) {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const kind = detectPreviewKind(filename, format);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    // Never fetch bytes we have no renderer for — an unsupported format shows the download prompt
    // straight away rather than pulling a 200MB installer over the wire to then refuse it.
    if (kind === "unsupported") {
      setLoading(false);
      return;
    }
    load()
      .then((b) => { if (alive) setBytes(b); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename, kind]);

  // Escape closes, and the body must not scroll behind the overlay.
  const onKey = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);
  useEffect(() => {
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onKey]);

  return (
    <div
      className="preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${filename} 미리보기`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="preview-card">
        <div className="preview-head">
          <div className="preview-title">
            <b>{filename || "문서"}</b>
            {subtitle && <span className="preview-sub">{subtitle}</span>}
          </div>
          <div className="preview-actions">
            <button className="sdui-button sdui-button-secondary" onClick={onDownload}>다운로드</button>
            <button className="wz-x" aria-label="닫기" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="preview-body">
          <Suspense fallback={<div className="center">미리보기 준비 중…</div>}>
            <DocPreview
              kind={kind}
              bytes={bytes}
              loading={loading}
              error={error}
              filename={filename}
              onDownload={onDownload}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
